import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { useHelpBundle } from './helpBundle';

// design_handoff_help_system/README.md: "One shared popover template filled
// from the live snapshot" — this is that template's data resolver, shared by
// the panel's limit article (screen 3a) and HelpMark's popover (step 2), so
// the two copies of "what happens at the limit" can never drift apart. Every
// number comes from EntitlementsContext + GET /plans; nothing is hardcoded.
export const PLAN_LIMIT_PREFIX = 'plan-limit.';

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

const findNextPlan = (plans, currentPlanId, featureKey) => {
  if (!Array.isArray(plans) || !plans.length) return null;
  const sorted = [...plans].sort((a, b) => (a.price?.amount || 0) - (b.price?.amount || 0));
  const idx = sorted.findIndex((p) => p._id === currentPlanId);
  if (idx === -1) return null;
  const currentEntitlement = sorted[idx].entitlements?.find((e) => e.featureKey === featureKey);
  const currentLimit = currentEntitlement ? currentEntitlement.limit : undefined;
  for (let i = idx + 1; i < sorted.length; i += 1) {
    const ent = sorted[i].entitlements?.find((e) => e.featureKey === featureKey);
    if (!ent) continue;
    if (ent.limit == null || currentLimit == null || ent.limit > currentLimit) {
      return { plan: sorted[i], limit: ent.limit };
    }
  }
  return null;
};

// `plans` (optional): the tenant-visible plan catalog from GET /plans
// (api/planClient.js `fetchPublicPlans`), used only to resolve "the next
// plan up" for the upgrade suggestion — omit it and everything else still
// resolves, just without a next-plan suggestion.
export const usePlanLimitContent = (featureKey, plans) => {
  const { t } = useTranslation('help');
  const { t: tt } = useTranslation();
  const bundle = useHelpBundle();
  const { usage, plan, subscription, periodEnd } = useEntitlements();
  const featureUsage = featureKey ? usage(featureKey) : null;

  return useMemo(() => {
    if (!featureKey || !featureUsage || featureUsage.used == null) return null;
    const { kind, limit, used } = featureUsage;
    const isMetered = kind === 'metered';

    // Dictionaries keyed by the feature key itself (which contains dots, e.g.
    // "invoices.issued.monthly") — read from the raw bundle, not `t()` (see
    // helpBundle.js for why).
    const featureLabel = bundle.planLimit?.featureName?.[featureKey] || featureKey;
    const featurePlural = bundle.planLimit?.featureNamePlural?.[featureKey] || featureKey;
    const featureLower = featureLabel.toLowerCase();
    const verb = bundle.planLimit?.featureVerb?.[featureKey] || 'add';
    // "issue" -> "issuing", "activate" -> "activating" (drop a trailing e),
    // "add"/"invite"/"receive"... -> "adding"/"inviting" (received handled by
    // the same trailing-e rule); good enough for this fixed, small verb set.
    const gerund = verb.endsWith('e') && !verb.endsWith('ee') ? `${verb.slice(0, -1)}ing` : `${verb}ing`;
    const verbLabel = `${gerund.charAt(0).toUpperCase()}${gerund.slice(1)}`;
    const stillWorks = bundle.planLimit?.featureStillWorks?.[featureKey] || '';
    const renewsOn = fmtDate(subscription?.currentPeriodEnd || periodEnd);
    const planName = plan?.name || '';
    const atLimit = limit != null && used >= limit;
    const next = findNextPlan(plans, plan?._id, featureKey);
    const nextPlan = next?.plan?.name || '';
    const nextPlanLimit = next ? (next.limit == null ? tt('unlimited', { defaultValue: 'unlimited' }) : next.limit) : '';

    const vars = {
      feature: featureLabel, featureLower, featurePlural,
      used, limit, renewsOn, planName, nextPlan, nextPlanLimit, verb, verbLabel, stillWorks,
    };

    return {
      kind, limit, used, atLimit, renewsOn, planName, nextPlan, nextPlanLimit,
      breadcrumb: t(isMetered ? 'planLimit.breadcrumbMetered' : 'planLimit.breadcrumbLimit', vars),
      title: t('planLimit.title', vars),
      meta: t('planLimit.meta', { minutes: 2 }),
      meterCaption: t('planLimit.meterCaption', vars),
      renewsCaption: t(isMetered ? 'planLimit.renewsCaptionMetered' : 'planLimit.renewsCaptionLimit', vars),
      whatCountsTitle: t('planLimit.whatCountsTitle'),
      whatCounts: t(isMetered ? 'planLimit.whatCountsMetered' : 'planLimit.whatCountsLimit', vars),
      whenResetsTitle: isMetered ? t('planLimit.whenResetsTitle') : null,
      whenResets: isMetered ? t('planLimit.whenResets') : null,
      atLimitTitle: t('planLimit.atLimitTitle'),
      atLimitText: t(isMetered ? 'planLimit.atLimitMetered' : 'planLimit.atLimitLimit', vars),
      needMoreTitle: t('planLimit.needMoreTitle'),
      needMore: t('planLimit.needMore'),
      comparePlans: t('planLimit.comparePlans'),
      adminOnlyNote: t('planLimit.adminOnlyNote'),
      usedOfLimitText: limit == null ? null : t('planLimit.usedOfLimit', vars),
      // Below the limit, the meter's "?" just states where you stand — the
      // urgent "you've used it all" wording is reserved for when it's true.
      // Unlimited (limit == null) gets its own neutral copy — never a blank
      // "{{limit}}" in the middle of a sentence.
      popoverTitle: limit == null
        ? t('planLimit.popoverTitleUnlimited', vars)
        : atLimit
          ? t(isMetered ? 'planLimit.popoverTitleMetered' : 'planLimit.popoverTitleLimit', vars)
          : t(isMetered ? 'planLimit.popoverTitleBelowLimitMetered' : 'planLimit.popoverTitleBelowLimitLimit', vars),
      popoverBody: limit == null
        ? t(isMetered ? 'planLimit.popoverBodyUnlimitedMetered' : 'planLimit.popoverBodyUnlimitedLimit', vars)
        : atLimit
          ? t(
            isMetered
              ? (next ? 'planLimit.popoverBodyWithNextMetered' : 'planLimit.popoverBodyNoNextMetered')
              : (next ? 'planLimit.popoverBodyWithNextLimit' : 'planLimit.popoverBodyNoNextLimit'),
            vars,
          )
          : t(isMetered ? 'planLimit.popoverBodyBelowLimitMetered' : 'planLimit.popoverBodyBelowLimitLimit', vars),
      tooltip: isMetered
        ? t('planLimit.tooltipLimitReached', vars)
        : t('planLimit.tooltipLimitReachedNoRenew', vars),
      snackbarText: t('planLimit.snackbarLimitReached', vars),
      seePlans: t('planLimit.seePlans'),
      readMore: t('planLimit.readMore'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureKey, featureUsage, plan, subscription, periodEnd, plans, t, tt, bundle]);
};

export default usePlanLimitContent;
